import { useCallback, useEffect, useState, type DragEvent, type FormEvent } from 'react';
import { Link } from '../components';
import { formatClock, formatPrice, pad2, plural } from '../format';
import { navigate } from '../router';
import { adminApi, errorText, type CourseDetail, type CourseSummary, type LessonRow, type ModuleRow } from './api';
import { MaterialsEditor } from './materials';
import { Card, Field, ImageUpload, SaveBar, Tag, Toggle, confirmAction, useSaver } from './ui';

/** Kurse: Liste oder, mit courseId, der Kurs-Editor mit Modulen und Lektionen (Konzept 5.1). */
export function CoursesPage({ courseId }: { courseId: string | null }) {
  return courseId ? <CourseEditor courseId={courseId} /> : <CourseList />;
}

const STATUS_LABEL = { draft: 'Entwurf', published: 'veröffentlicht', archived: 'archiviert' } as const;

function CourseList() {
  const [courses, setCourses] = useState<CourseSummary[] | null>(null);
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void adminApi.listCourses().then(setCourses).catch(() => setCourses([]));
  }, []);

  async function create(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    try {
      const c = await adminApi.createCourse(title.trim());
      navigate(`/verwaltung/kurse/${c.id}`);
    } catch (err) {
      setError(errorText(err));
    }
  }

  async function move(index: number, dir: -1 | 1) {
    if (!courses) return;
    const next = [...courses];
    const [item] = next.splice(index, 1);
    next.splice(index + dir, 0, item!);
    setCourses(next);
    await adminApi.reorderCourses(next.map((c) => c.id)).catch(() => undefined);
  }

  return (
    <>
      <div className="adm-head">
        <div>
          <h1>Kurse</h1>
          <p className="muted">Alles, was deine Kunden lernen können. Die Reihenfolge hier ist die Reihenfolge in der Bibliothek.</p>
        </div>
      </div>
      <Card as="form" onSubmit={create} title="Neuen Kurs anlegen">
        <div className="row">
          <input type="text" placeholder="Titel des Kurses" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
          <button type="submit" className="button" disabled={!title.trim()}>
            Anlegen
          </button>
        </div>
        {error && <div className="error">{error}</div>}
      </Card>
      {courses === null ? (
        <p className="muted">Wird geladen …</p>
      ) : courses.length === 0 ? (
        <p className="muted">Noch kein Kurs. Leg oben den ersten an.</p>
      ) : (
        <div className="adm-list">
          {courses.map((c, i) => (
            <div key={c.id} className="adm-row">
              <div className="adm-row__thumb" style={c.imageUrl ? { backgroundImage: `url(${c.imageUrl})` } : undefined} />
              <div className="adm-row__body">
                <Link href={`/verwaltung/kurse/${c.id}`} className="adm-row__title">
                  {c.title.replace('\n', ' ')}
                </Link>
                <span className="muted small">
                  {plural(c.lessons, 'Lektion', 'Lektionen')} · {plural(c.customers, 'Kunde', 'Kunden')} · /kurs/{c.slug}
                </span>
              </div>
              <Tag tone={c.status === 'published' ? 'ok' : c.status === 'draft' ? 'warn' : 'muted'}>{STATUS_LABEL[c.status]}</Tag>
              {!c.inCatalog && <Tag tone="muted">nicht im Katalog</Tag>}
              <div className="adm-row__actions">
                <button type="button" className="icon-button" disabled={i === 0} onClick={() => void move(i, -1)} aria-label="Nach oben">↑</button>
                <button type="button" className="icon-button" disabled={i === courses.length - 1} onClick={() => void move(i, 1)} aria-label="Nach unten">↓</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// --- Kurs-Editor -----------------------------------------------------------------------------------------

function CourseEditor({ courseId }: { courseId: string }) {
  const [course, setCourse] = useState<CourseDetail | null | undefined>(undefined);
  const reload = useCallback(() => void adminApi.getCourse(courseId).then(setCourse).catch(() => setCourse(null)), [courseId]);
  useEffect(reload, [reload]);

  if (course === undefined) return <p className="muted">Wird geladen …</p>;
  if (course === null)
    return (
      <>
        <h1>Kurs nicht gefunden</h1>
        <Link href="/verwaltung" className="button button--secondary">Zur Kursliste</Link>
      </>
    );

  return (
    <>
      <div className="adm-head">
        <div>
          <Link href="/verwaltung" className="muted small">← Alle Kurse</Link>
          <h1>{course.title.replace('\n', ' ')}</h1>
          <p className="muted">
            <a href={`/kurs/${course.slug}`} target="_blank" rel="noopener">/kurs/{course.slug} ↗</a> · {plural(course.lessons, 'Lektion', 'Lektionen')} · {plural(course.customers, 'Kunde', 'Kunden')}
          </p>
        </div>
        <Tag tone={course.status === 'published' ? 'ok' : course.status === 'draft' ? 'warn' : 'muted'}>{STATUS_LABEL[course.status]}</Tag>
      </div>
      <CourseBasics course={course} onChanged={setCourse} />
      <Structure course={course} onChanged={setCourse} />
      <Card title="Materialien zum ganzen Kurs">
        <p className="muted small">Workbooks und Links, die zu keiner einzelnen Lektion gehören – erscheinen unter „Alle Materialien“.</p>
        <MaterialsEditor courseId={course.id} lessonId={null} materials={course.materials} onChanged={reload} />
      </Card>
      <Card title="Produkte, die diesen Kurs freischalten">
        {course.products.length === 0 ? (
          <p className="muted small">Noch kein Produkt. Ohne Produkt kann niemand den Kurs kaufen oder freigeschaltet bekommen.</p>
        ) : (
          <ul className="plain-list">
            {course.products.map((p) => (
              <li key={p.id}>
                <strong>{p.title}</strong> <span className="muted">· {formatPrice(p.priceCents)}</span>
              </li>
            ))}
          </ul>
        )}
        <Link href="/verwaltung/produkte" className="button button--secondary button--small">Produkte verwalten</Link>
      </Card>
      <DangerZone course={course} />
    </>
  );
}

function CourseBasics({ course, onChanged }: { course: CourseDetail; onChanged: (c: CourseDetail) => void }) {
  const [form, setForm] = useState({ title: course.title, subtitle: course.subtitle ?? '', kicker: course.kicker ?? '', description: course.description ?? '', slug: course.slug, status: course.status, inCatalog: course.inCatalog, certificateEnabled: course.certificateEnabled });
  const saver = useSaver();
  useEffect(() => setForm({ title: course.title, subtitle: course.subtitle ?? '', kicker: course.kicker ?? '', description: course.description ?? '', slug: course.slug, status: course.status, inCatalog: course.inCatalog, certificateEnabled: course.certificateEnabled }), [course]);
  const dirty = form.title !== course.title || form.subtitle !== (course.subtitle ?? '') || form.kicker !== (course.kicker ?? '') || form.description !== (course.description ?? '') || form.slug !== course.slug || form.status !== course.status || form.inCatalog !== course.inCatalog || form.certificateEnabled !== course.certificateEnabled;

  return (
    <Card
      as="form"
      title="Kurs"
      onSubmit={(e) => {
        e.preventDefault();
        void saver.run(async () => onChanged(await adminApi.updateCourse(course.id, form)));
      }}
    >
      <div className="grid2">
        <Field label="Titel" id="c-title" hint="Ein Zeilenumbruch teilt den Titel in zwei Zeilen – die zweite erscheint in der Akzentfarbe.">
          <textarea id="c-title" rows={2} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={200} required />
        </Field>
        <Field label="Untertitel" id="c-subtitle">
          <input id="c-subtitle" type="text" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} maxLength={300} />
        </Field>
        <Field label="Kleine Zeile über dem Titel" id="c-kicker" hint="z. B. „Der komplette Kurs“ oder „Neu im Programm“.">
          <input id="c-kicker" type="text" value={form.kicker} onChange={(e) => setForm({ ...form, kicker: e.target.value })} maxLength={80} />
        </Field>
        <Field label="Adresse" id="c-slug" hint={`Der Kurs ist erreichbar unter /kurs/${form.slug || '…'}`}>
          <input id="c-slug" type="text" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} maxLength={80} pattern="[a-z0-9][a-z0-9-]*" />
        </Field>
      </div>
      <Field label="Beschreibung" id="c-description" hint="Steht auf der Kursübersicht unter dem Titel.">
        <textarea id="c-description" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} maxLength={5000} />
      </Field>
      <div className="grid2">
        <Field label="Status" id="c-status" hint="Nur veröffentlichte Kurse sind für Kunden sichtbar.">
          <select id="c-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as CourseDetail['status'] })}>
            <option value="draft">Entwurf</option>
            <option value="published">Veröffentlicht</option>
            <option value="archived">Archiviert</option>
          </select>
        </Field>
        <div className="toggles">
          <Toggle checked={form.inCatalog} onChange={(v) => setForm({ ...form, inCatalog: v })} label="Im Katalog zeigen" hint="Auch wer den Kurs nicht besitzt, sieht ihn in der Bibliothek – mit Schloss und Preis." />
          <Toggle checked={form.certificateEnabled} onChange={(v) => setForm({ ...form, certificateEnabled: v })} label="Zertifikat bei Abschluss" hint="Kommt in Stufe 2." />
        </div>
      </div>
      <Field label="Kursbild">
        <ImageUpload
          current={course.imageUrl}
          alt="Kursbild"
          previewClass="image-upload__preview--wide"
          hint="Querformat, mindestens 1600 × 900. JPG, PNG oder WebP bis 5 MB. Ohne Bild zeigt die Plattform eine Farbfläche in deinen Farben."
          onUpload={async (file) => onChanged(await adminApi.setCourseImage(course.id, file))}
          onRemove={async () => onChanged(await adminApi.removeCourseImage(course.id))}
        />
      </Field>
      <SaveBar saver={saver} dirty={dirty} />
    </Card>
  );
}

// --- Module und Lektionen mit Drag & Drop -----------------------------------------------------------------

type Drag = { kind: 'lesson'; id: string; fromModule: string } | { kind: 'module'; id: string } | null;

function Structure({ course, onChanged }: { course: CourseDetail; onChanged: (c: CourseDetail) => void }) {
  const [drag, setDrag] = useState<Drag>(null);
  const [over, setOver] = useState<{ moduleId: string; index: number } | { module: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newModule, setNewModule] = useState('');

  const modules = course.modules;
  let running = 0;

  async function apply(next: ModuleRow[]) {
    setError(null);
    onChanged({ ...course, modules: next });
    try {
      onChanged(await adminApi.reorder(course.id, next.map((m) => ({ id: m.id, lessons: m.lessons.map((l) => l.id) }))));
    } catch (e) {
      setError(errorText(e));
      onChanged(await adminApi.getCourse(course.id));
    }
  }

  function dropLesson(targetModule: string, index: number) {
    if (!drag || drag.kind !== 'lesson') return;
    const next = modules.map((m) => ({ ...m, lessons: [...m.lessons] }));
    const from = next.find((m) => m.id === drag.fromModule)!;
    const lessonIdx = from.lessons.findIndex((l) => l.id === drag.id);
    const [lesson] = from.lessons.splice(lessonIdx, 1);
    const to = next.find((m) => m.id === targetModule)!;
    let insertAt = index;
    if (from === to && lessonIdx < index) insertAt -= 1;
    to.lessons.splice(insertAt, 0, { ...lesson!, moduleId: targetModule });
    void apply(next);
  }

  function dropModule(targetIndex: number) {
    if (!drag || drag.kind !== 'module') return;
    const next = [...modules];
    const fromIdx = next.findIndex((m) => m.id === drag.id);
    const [m] = next.splice(fromIdx, 1);
    next.splice(fromIdx < targetIndex ? targetIndex - 1 : targetIndex, 0, m!);
    void apply(next);
  }

  function moveLesson(moduleId: string, index: number, dir: -1 | 1) {
    const next = modules.map((m) => ({ ...m, lessons: [...m.lessons] }));
    const mi = next.findIndex((m) => m.id === moduleId);
    const m = next[mi]!;
    const target = index + dir;
    if (target >= 0 && target < m.lessons.length) {
      const [l] = m.lessons.splice(index, 1);
      m.lessons.splice(target, 0, l!);
    } else {
      // Über die Modulgrenze hinaus: ans Ende des vorigen bzw. an den Anfang des nächsten Moduls.
      const neighbor = next[mi + dir];
      if (!neighbor) return;
      const [l] = m.lessons.splice(index, 1);
      if (dir === -1) neighbor.lessons.push({ ...l!, moduleId: neighbor.id });
      else neighbor.lessons.unshift({ ...l!, moduleId: neighbor.id });
    }
    void apply(next);
  }

  function moveModule(index: number, dir: -1 | 1) {
    const next = [...modules];
    const [m] = next.splice(index, 1);
    next.splice(index + dir, 0, m!);
    void apply(next);
  }

  const onDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  return (
    <Card
      title="Module und Lektionen"
      right={<span className="muted small">Ziehen zum Sortieren – oder die Pfeile nutzen</span>}
    >
      {error && <div className="error">{error}</div>}
      <div className="structure">
        {modules.map((m, mi) => {
          const first = running + 1;
          running += m.lessons.length;
          return (
            <div key={m.id}>
              {drag?.kind === 'module' && drag.id !== m.id && (
                <div className={`dropzone${over && 'module' in over && over.module === m.id ? ' dropzone--over' : ''}`} onDragOver={(e) => { onDragOver(e); setOver({ module: m.id }); }} onDrop={() => dropModule(mi)} />
              )}
              <ModuleBlock
                module={m}
                index={mi}
                count={modules.length}
                firstNumber={first}
                course={course}
                onChanged={onChanged}
                drag={drag}
                over={over}
                setOver={setOver}
                onDragStartModule={() => setDrag({ kind: 'module', id: m.id })}
                onDragStartLesson={(l) => setDrag({ kind: 'lesson', id: l.id, fromModule: m.id })}
                onDragEnd={() => { setDrag(null); setOver(null); }}
                onDropLesson={(index) => dropLesson(m.id, index)}
                onMoveLesson={(index, dir) => moveLesson(m.id, index, dir)}
                onMoveModule={(dir) => moveModule(mi, dir)}
              />
            </div>
          );
        })}
        {drag?.kind === 'module' && <div className={`dropzone${over && 'module' in over && over.module === 'end' ? ' dropzone--over' : ''}`} onDragOver={(e) => { onDragOver(e); setOver({ module: 'end' }); }} onDrop={() => dropModule(modules.length)} />}
      </div>
      <form
        className="row"
        style={{ marginTop: 16 }}
        onSubmit={(e) => {
          e.preventDefault();
          void adminApi.createModule(course.id, newModule.trim() || `Modul ${modules.length + 1}`).then((c) => { onChanged(c); setNewModule(''); }).catch((err: unknown) => setError(errorText(err)));
        }}
      >
        <input type="text" placeholder="Neues Modul (Titel)" value={newModule} onChange={(e) => setNewModule(e.target.value)} maxLength={200} />
        <button type="submit" className="button button--secondary">Modul hinzufügen</button>
      </form>
    </Card>
  );
}

interface ModuleBlockProps {
  module: ModuleRow;
  index: number;
  count: number;
  firstNumber: number;
  course: CourseDetail;
  onChanged: (c: CourseDetail) => void;
  drag: Drag;
  over: { moduleId: string; index: number } | { module: string } | null;
  setOver: (o: { moduleId: string; index: number } | null) => void;
  onDragStartModule: () => void;
  onDragStartLesson: (l: LessonRow) => void;
  onDragEnd: () => void;
  onDropLesson: (index: number) => void;
  onMoveLesson: (index: number, dir: -1 | 1) => void;
  onMoveModule: (dir: -1 | 1) => void;
}

function ModuleBlock({ module: m, index, count, firstNumber, course, onChanged, drag, over, setOver, onDragStartModule, onDragStartLesson, onDragEnd, onDropLesson, onMoveLesson, onMoveModule }: ModuleBlockProps) {
  const [editing, setEditing] = useState(false);
  const [newLesson, setNewLesson] = useState('');
  const [error, setError] = useState<string | null>(null);
  const isOver = (i: number) => over !== null && 'moduleId' in over && over.moduleId === m.id && over.index === i;
  const lessonDrag = drag?.kind === 'lesson';

  return (
    <section className="module-block" onDragEnd={onDragEnd}>
      <header className="module-block__head">
        <span className="drag-handle" draggable onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; onDragStartModule(); }} title="Modul ziehen" aria-hidden="true">⋮⋮</span>
        <div className="module-block__title">
          <span className="kicker">Modul {index + 1}</span>
          <strong>{m.title}</strong>
          {m.subtitle && <span className="muted small"> – {m.subtitle}</span>}
        </div>
        <span className="muted small">{releaseLabel(m)}</span>
        <div className="adm-row__actions">
          <button type="button" className="icon-button" disabled={index === 0} onClick={() => onMoveModule(-1)} aria-label="Modul nach oben">↑</button>
          <button type="button" className="icon-button" disabled={index === count - 1} onClick={() => onMoveModule(1)} aria-label="Modul nach unten">↓</button>
          <button type="button" className="button button--secondary button--small" onClick={() => setEditing((v) => !v)}>
            {editing ? 'Schließen' : 'Bearbeiten'}
          </button>
        </div>
      </header>
      {editing && <ModuleForm module={m} course={course} onChanged={onChanged} onDone={() => setEditing(false)} />}
      <ol className="lesson-list">
        {m.lessons.map((l, i) => (
          <li key={l.id}>
            {lessonDrag && <div className={`dropzone dropzone--thin${isOver(i) ? ' dropzone--over' : ''}`} onDragOver={(e) => { e.preventDefault(); setOver({ moduleId: m.id, index: i }); }} onDrop={() => onDropLesson(i)} />}
            <div className={`lesson-item${drag?.kind === 'lesson' && drag.id === l.id ? ' lesson-item--dragging' : ''}`} draggable onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; onDragStartLesson(l); }}>
              <span className="drag-handle" aria-hidden="true">⋮⋮</span>
              <span className="lesson-item__number">{pad2(firstNumber + i)}</span>
              <Link href={`/verwaltung/kurse/${course.id}/lektion/${l.id}`} className="lesson-item__title">
                {l.title}
              </Link>
              <span className="lesson-item__meta muted small">
                {l.video ? (l.video.status === 'ready' ? formatClock(l.video.durationSeconds ?? 0) : l.video.status === 'failed' || l.video.status === 'rejected' ? 'Video fehlgeschlagen' : `Video ${l.video.progressPercent} %`) : 'kein Video'}
                {l.chapters > 0 && ` · ${plural(l.chapters, 'Sprungmarke', 'Sprungmarken')}`}
                {l.materials > 0 && ` · ${plural(l.materials, 'Material', 'Materialien')}`}
                {l.preview && ' · Vorschau'}
              </span>
              <div className="adm-row__actions">
                <button type="button" className="icon-button" disabled={index === 0 && i === 0} onClick={() => onMoveLesson(i, -1)} aria-label="Nach oben">↑</button>
                <button type="button" className="icon-button" disabled={index === count - 1 && i === m.lessons.length - 1} onClick={() => onMoveLesson(i, 1)} aria-label="Nach unten">↓</button>
              </div>
            </div>
          </li>
        ))}
        {lessonDrag && <li><div className={`dropzone dropzone--thin${isOver(m.lessons.length) ? ' dropzone--over' : ''}`} onDragOver={(e) => { e.preventDefault(); setOver({ moduleId: m.id, index: m.lessons.length }); }} onDrop={() => onDropLesson(m.lessons.length)} /></li>}
      </ol>
      <form
        className="row row--compact"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          void adminApi.createLesson(m.id, newLesson.trim() || 'Neue Lektion').then((r) => { onChanged(r.course); setNewLesson(''); navigate(`/verwaltung/kurse/${course.id}/lektion/${r.lessonId}`); }).catch((err: unknown) => setError(errorText(err)));
        }}
      >
        <input type="text" placeholder="Neue Lektion (Titel)" value={newLesson} onChange={(e) => setNewLesson(e.target.value)} maxLength={200} />
        <button type="submit" className="button button--small">Lektion hinzufügen</button>
      </form>
      {error && <div className="error">{error}</div>}
    </section>
  );
}

function releaseLabel(m: ModuleRow): string {
  if (m.releaseKind === 'days_after_access') return `frei ${m.releaseDays} Tage nach Freischaltung`;
  if (m.releaseKind === 'date' && m.releaseAt) return `frei ab ${new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(m.releaseAt))}`;
  return 'sofort frei';
}

function ModuleForm({ module: m, course, onChanged, onDone }: { module: ModuleRow; course: CourseDetail; onChanged: (c: CourseDetail) => void; onDone: () => void }) {
  const [form, setForm] = useState({ title: m.title, subtitle: m.subtitle ?? '', releaseKind: m.releaseKind, releaseDays: m.releaseDays ?? 7, releaseAt: m.releaseAt ? m.releaseAt.slice(0, 10) : '' });
  const saver = useSaver();
  return (
    <form
      className="module-form"
      onSubmit={(e) => {
        e.preventDefault();
        void saver.run(async () => {
          onChanged(await adminApi.updateModule(m.id, { title: form.title, subtitle: form.subtitle || null, releaseKind: form.releaseKind, releaseDays: form.releaseKind === 'days_after_access' ? Number(form.releaseDays) : null, releaseAt: form.releaseKind === 'date' && form.releaseAt ? new Date(`${form.releaseAt}T06:00:00`).toISOString() : null }));
          onDone();
        });
      }}
    >
      <div className="grid2">
        <Field label="Titel" id={`m-title-${m.id}`}>
          <input id={`m-title-${m.id}`} type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required maxLength={200} />
        </Field>
        <Field label="Untertitel" id={`m-sub-${m.id}`}>
          <input id={`m-sub-${m.id}`} type="text" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} maxLength={300} />
        </Field>
        <Field label="Freischaltung" id={`m-rel-${m.id}`} hint="Zeitliche Freischaltung hält Lernende im Rhythmus und schützt vor Durchklicken.">
          <select id={`m-rel-${m.id}`} value={form.releaseKind} onChange={(e) => setForm({ ...form, releaseKind: e.target.value as ModuleRow['releaseKind'] })}>
            <option value="immediate">Sofort mit dem Kurs</option>
            <option value="days_after_access">X Tage nach Freischaltung des Kurses</option>
            <option value="date">Ab einem festen Datum</option>
          </select>
        </Field>
        {form.releaseKind === 'days_after_access' && (
          <Field label="Tage nach Freischaltung" id={`m-days-${m.id}`}>
            <input id={`m-days-${m.id}`} type="number" min={0} max={3650} value={form.releaseDays} onChange={(e) => setForm({ ...form, releaseDays: Number(e.target.value) })} />
          </Field>
        )}
        {form.releaseKind === 'date' && (
          <Field label="Frei ab (6 Uhr morgens)" id={`m-date-${m.id}`}>
            <input id={`m-date-${m.id}`} type="date" value={form.releaseAt} onChange={(e) => setForm({ ...form, releaseAt: e.target.value })} required />
          </Field>
        )}
      </div>
      <SaveBar saver={saver} dirty>
        <button type="button" className="link-button" onClick={() => { if (confirmAction(`Modul „${m.title}“ mit allen Lektionen löschen? Das lässt sich nicht rückgängig machen.`)) void adminApi.deleteModule(m.id).then(onChanged).catch((e: unknown) => saver.setError(errorText(e))); }}>
          Modul löschen
        </button>
      </SaveBar>
    </form>
  );
}

function DangerZone({ course }: { course: CourseDetail }) {
  const [error, setError] = useState<string | null>(null);
  return (
    <Card title="Weitere Aktionen">
      <div className="row-actions">
        <button type="button" className="button button--secondary button--small" onClick={() => void adminApi.duplicateCourse(course.id).then((c) => navigate(`/verwaltung/kurse/${c.id}`)).catch((e: unknown) => setError(errorText(e)))}>
          Kurs duplizieren
        </button>
        <button type="button" className="link-button link-button--danger" onClick={() => { if (confirmAction(`Kurs „${course.title.replace('\n', ' ')}“ endgültig löschen? Alle Lektionen, Materialien und der Fortschritt der Kunden gehen verloren.`)) void adminApi.deleteCourse(course.id).then(() => navigate('/verwaltung')).catch((e: unknown) => setError(errorText(e))); }}>
          Kurs löschen
        </button>
      </div>
      {error && <div className="error">{error}</div>}
    </Card>
  );
}
